import { useState } from "react";
import { api } from "../api";
import { articleApi, type ResourceRole } from "../api/articleUnits";
import { ApiError } from "../api/types";
import type { ProposedRevision } from "../api/types";
import { Button, SelectField, TextField } from "../compose/controls";
import { Dialog } from "../compose/Dialog";
import { Repeat2 } from "../compose/Icon";
import { useAsync } from "../lib/useAsync";

/**
 * NewProposalDialog — staff/editor seeds a TEST proposal while the agent-loop
 * that authors real proposals is a later track (the store + the human accept/
 * reject surface exist now, per the brief). Article-first (design §3): the target
 * is chosen as an ARTICLE UNIT + a resource slot (brief / research / article),
 * which the dialog resolves to the resource's `document_id` before POSTing to the
 * per-document create endpoint (`POST /api/documents/:id/proposals`, unchanged).
 */

// The reviewer agents (the same council the delivery gate names) stand in as the
// proposing authors until the agent-loop lands.
const AGENTS = [
  { value: "briefing-fidelity", label: "briefing-fidelity" },
  { value: "quality", label: "quality" },
  { value: "seo", label: "seo" },
  { value: "geo-leak-sourcing", label: "geo-leak-sourcing" },
  { value: "tone-grey-market", label: "tone-grey-market" },
];

// The three resource slots of an article unit (design §1.2), in carve order.
const RESOURCE_ROLES: { value: ResourceRole; label: string }[] = [
  { value: "brief", label: "Brief" },
  { value: "research", label: "Research" },
  { value: "article", label: "Article" },
];

function Textarea({
  label,
  value,
  onChange,
  placeholder,
  testid,
  rows = 3,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  testid?: string;
  rows?: number;
}) {
  const id = `t-${label.replace(/\s+/g, "-").toLowerCase()}`;
  return (
    <div className="field">
      <label className="field__label" htmlFor={id}>
        {label}
      </label>
      <textarea
        id={id}
        className="field__input proposal-textarea"
        rows={rows}
        value={value}
        placeholder={placeholder}
        data-testid={testid}
        onChange={(e) => onChange(e.target.value)}
      />
    </div>
  );
}

export function NewProposalDialog({
  slug,
  onClose,
  onCreated,
}: {
  slug: string;
  onClose: () => void;
  onCreated: (p: ProposedRevision) => void;
}) {
  const units = useAsync(() => articleApi.listArticleUnits(slug), [slug]);
  const [unitId, setUnitId] = useState("");
  const [role, setRole] = useState<ResourceRole>("article");
  const [blockRef, setBlockRef] = useState("b1");
  const [agent, setAgent] = useState(AGENTS[0].value);
  const [baseBody, setBaseBody] = useState("");
  const [proposedBody, setProposedBody] = useState("");
  const [rationale, setRationale] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const effectiveUnit = unitId || units.data?.[0]?.id || "";

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!effectiveUnit) {
      setError("Pick an article unit to propose against.");
      return;
    }
    if (!proposedBody.trim()) {
      setError("Enter the proposed text.");
      return;
    }
    setBusy(true);
    try {
      // Resolve the chosen unit + resource slot to the underlying document id —
      // the create endpoint is per-document (design §3). A unit missing that slot
      // is a clear, recoverable error (never a silent wrong-doc proposal).
      const unit = await articleApi.getArticleUnit(slug, String(effectiveUnit));
      const resource = unit.resources[role];
      if (!resource) {
        setError(`This unit has no ${role} resource yet.`);
        return;
      }
      const created = await api.createProposal(String(resource.doc_id), {
        block_ref: blockRef.trim() || "b1",
        base_body: baseBody,
        proposed_body: proposedBody,
        rationale: rationale.trim(),
        author_agent: agent,
      });
      onCreated(created);
      onClose();
    } catch (err) {
      setError(
        err instanceof ApiError ? err.message : "Could not create the proposal.",
      );
    } finally {
      setBusy(false);
    }
  }

  const unitOptions =
    units.data?.map((u) => ({ value: u.id, label: u.title })) ?? [];

  return (
    <Dialog
      title="New test proposal"
      subtitle="Seed an agent-style proposed revision against an article-unit resource (staff/editor)."
      onClose={onClose}
      testid="new-proposal-dialog"
    >
      <form onSubmit={submit}>
        {error ? (
          <div className="auth__error" role="alert">
            {error}
          </div>
        ) : null}

        <SelectField
          label="Article unit"
          options={
            unitOptions.length ? unitOptions : [{ value: "", label: "Loading…" }]
          }
          value={effectiveUnit}
          data-testid="proposal-unit"
          onChange={(e) => setUnitId(e.target.value)}
        />
        <div className="proposal-form__row">
          <SelectField
            label="Resource"
            options={RESOURCE_ROLES}
            value={role}
            data-testid="proposal-role"
            onChange={(e) => setRole(e.target.value as ResourceRole)}
          />
          <TextField
            label="Block"
            value={blockRef}
            data-testid="proposal-block"
            onChange={(e) => setBlockRef(e.target.value)}
          />
        </div>
        <SelectField
          label="Author agent"
          options={AGENTS}
          value={agent}
          data-testid="proposal-agent"
          onChange={(e) => setAgent(e.target.value)}
        />
        <Textarea
          label="Before (current text)"
          value={baseBody}
          onChange={setBaseBody}
          placeholder="The block text the change replaces."
          testid="proposal-base"
        />
        <Textarea
          label="Proposed text"
          value={proposedBody}
          onChange={setProposedBody}
          placeholder="What the agent proposes instead."
          testid="proposal-proposed"
        />
        <Textarea
          label="Rationale"
          value={rationale}
          onChange={setRationale}
          rows={2}
          placeholder="Why this change (cites the briefing, a test result, …)."
          testid="proposal-rationale"
        />

        <div className="dialog__actions">
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            disabled={busy}
            data-testid="proposal-submit"
            icon={<Repeat2 size={15} strokeWidth={1.5} />}
          >
            {busy ? "Creating…" : "Create proposal"}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
